<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search attributes..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      :active-filter-count="activeFilterCount"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchAttributes"
      @page-change="goToPage"
      @clear-filters="clearFilters"
    >
      <template #filters>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Type</label>
          <select v-model="filters.type">
            <option value="">All</option>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
          </select>
        </div>
      </template>
      <template #actions>
        <button v-if="$checkPermission('attributes.create')" @click="openCreate" class="btn-primary">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Attribute
        </button>
      </template>
    </ListToolbar>

    <!-- Table -->
    <div v-if="loading || items.length" :class="{ 'animate-fade-in': !loading }" class="bg-white rounded-xl border border-gray-200/60 table-scroll card-shadow">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Name</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden sm:table-cell">Code</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">Type</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Modules</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Actions</th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr v-for="i in 6" :key="'skel-' + i" class="border-b border-gray-50">
            <td class="px-6 py-4">
              <div class="space-y-2"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" /></div>
            </td>
            <td class="px-6 py-4 hidden sm:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-20" /></td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-16" /></td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-6 bg-gray-100 rounded-md skeleton-pulse w-10" /></td>
            <td class="px-6 py-4"><div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-20 ml-auto" /></td>
          </tr>
        </tbody>
        <tbody v-else class="divide-y divide-gray-50">
          <tr v-for="item in items" :key="item._id" class="hover:bg-gray-50/50 transition-colors">
            <td class="px-6 py-4">
              <p class="text-[14px] text-gray-900">{{ item.name }}</p>
            </td>
            <td class="px-6 py-4 hidden sm:table-cell">
              <span class="text-sm font-data text-gray-500">{{ item.field_code }}</span>
            </td>
            <td class="px-6 py-4 hidden md:table-cell">
              <span class="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-semibold bg-gray-100 text-gray-500 uppercase">{{ item.type }}</span>
            </td>
            <td class="px-6 py-4 hidden lg:table-cell">
              <div class="flex flex-wrap gap-1">
                <span v-for="mod in (item.modules || [])" :key="typeof mod === 'object' ? mod.code : mod" class="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-[var(--color-primary-light)] text-[var(--color-primary)]">{{ typeof mod === 'object' ? mod.name : mod }}</span>
                <span v-if="!item.modules?.length" class="text-sm text-gray-300">—</span>
              </div>
            </td>
            <td class="px-6 py-4 text-right">
              <div class="flex items-center justify-end gap-1">
                <ActionsMenu :items="attributeActions(item)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="!loading && (search || filters.type)" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching attributes found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
    </div>

    <!-- Empty: no records -->
    <div v-else-if="!loading" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No custom attributes yet</p>
      <p class="text-sm text-gray-500 mt-1">Create attributes to add custom fields to your resources</p>
    </div>

    <!-- Create/Edit Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">{{ editItem ? 'Edit Attribute' : 'New Attribute' }}</h3>
              <button @click="showModal = false" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form @submit.prevent="saveAttribute" class="flex-1 flex flex-col overflow-hidden">
              <div class="flex-1 overflow-y-auto p-6 pt-5 space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Name</label>
                <input v-model="form.name" type="text" required placeholder="Attribute name" class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Type</label>
                <select v-model="form.type" required>
                  <option value="">Select type...</option>
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-2">Modules</label>
                <div class="space-y-3">
                  <div v-for="opt in moduleOptions" :key="opt.code" class="flex items-center justify-between">
                    <div>
                      <p class="text-sm font-medium text-gray-700">{{ opt.name }}</p>
                      <p class="text-sm text-gray-400">{{ opt.description }}</p>
                    </div>
                    <ToggleSwitch :model-value="isModuleSelected(opt.code)" @update:model-value="toggleModule(opt)" />
                  </div>
                </div>
              </div>
              </div>
              <div class="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                <button type="button" @click="showModal = false" class="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button type="submit" :disabled="saving" class="px-5 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-50">{{ saving ? 'Saving...' : 'Save' }}</button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <ConfirmModal :show="showDeleteModal" :loading="deleting" title="Delete Attribute" :message="`Delete '${deleteItem?.name}'? This cannot be undone.`" confirm-text="Delete" :danger="true" @confirm="deleteAttribute" @cancel="showDeleteModal = false" />
  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import ConfirmModal from '../../components/ConfirmModal.vue'
import ToggleSwitch from '../../components/ToggleSwitch.vue'
import icons from '../../components/action-icons.js'
import api from '../../services/api.js'
import { notifySuccess } from '../../services/notifications.js'

export default {
  name: 'AttributesList',
  components: { ListToolbar, ActionsMenu, ConfirmModal, ToggleSwitch },
  data() {
    return {
      loading: true, items: [], search: '', filters: { type: '' }, sort: 'created:-1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' }, { value: 'name:-1', label: 'Name Z-A' },
        { value: 'updated:1', label: 'Last Updated (oldest)' }, { value: 'updated:-1', label: 'Last Updated (newest)' },
        { value: 'created:1', label: 'Created (oldest)' }, { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 10, page: 1, total: 0 },
      showModal: false, showDeleteModal: false, deleting: false, saving: false,
      editItem: null, deleteItem: null, form: { name: '', type: '', modules: [] },
      moduleOptions: [
        { name: 'Schedules', code: 'schedules', description: 'Apply to scheduled livestreams' },
        { name: 'Medias', code: 'medias', description: 'Apply to media library items' }
      ]
    }
  },
  computed: {
    activeFilterCount() { return Object.values(this.filters).filter(v => v !== '').length }
  },
  watch: {
    search() { this.pagination.page = 1; this.fetchAttributes() },
    sort() { this.pagination.page = 1; this.fetchAttributes() },
    filters: { deep: true, handler() { this.pagination.page = 1; this.fetchAttributes() } }
  },
  mounted() { this.fetchAttributes() },
  methods: {
    clearFilters() { this.filters = { type: '' } },
    async fetchAttributes(count = true) {
      try {
        const params = { limit: this.pagination.limit, page: this.pagination.page }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (this.filters.type) params.type = this.filters.type
        if (count) params.count = true
        const res = await api.get('/attributes', { params })
        this.items = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
      } catch { /* keep */ } finally { this.loading = false }
    },
    goToPage(page) { this.pagination.page = page; this.fetchAttributes(false) },
    openCreate() { this.editItem = null; this.form = { name: '', type: '', modules: [] }; this.showModal = true },
    openEdit(item) {
      this.editItem = item
      this.form = { name: item.name, type: item.type, modules: [...(item.modules || [])] }
      this.showModal = true
    },
    isModuleSelected(code) {
      return this.form.modules.some(m => (typeof m === 'object' ? m.code : m) === code)
    },
    toggleModule(opt) {
      const idx = this.form.modules.findIndex(m => (typeof m === 'object' ? m.code : m) === opt.code)
      if (idx >= 0) {
        this.form.modules.splice(idx, 1)
      } else {
        this.form.modules.push({ name: opt.name, code: opt.code })
      }
    },
    async saveAttribute() {
      this.saving = true
      try {
        if (this.editItem) {
          await api.put(`/attributes/${this.editItem._id}`, { attribute: this.form })
          notifySuccess('Attribute updated')
        } else {
          await api.post('/attributes', { attribute: this.form })
          notifySuccess('Attribute created')
        }
        this.showModal = false; this.fetchAttributes()
      } catch { /* toast */ }
      this.saving = false
    },
    attributeActions(item) {
      return [
        { label: 'Edit', show: this.$checkPermission('attributes.update'), action: () => this.openEdit(item), icon: icons.edit },
        { divider: true },
        { label: 'Delete', show: this.$checkPermission('attributes.delete'), danger: true, action: () => this.confirmDelete(item), icon: icons.delete }
      ]
    },
    confirmDelete(item) { this.deleteItem = item; this.showDeleteModal = true },
    async deleteAttribute() {
      this.deleting = true
      try { await api.delete(`/attributes/${this.deleteItem._id}`); notifySuccess('Attribute deleted'); this.fetchAttributes() } catch { /* toast */ }
      this.showDeleteModal = false; this.deleteItem = null
      this.deleting = false
    },
  }
}
</script>
<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
